import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { showToast, toast } from '@/components/Notifications/Toast'

export interface KeyboardShortcut {
  key: string // e.g., "n", "/", "s"
  ctrl?: boolean
  shift?: boolean
  alt?: boolean
  meta?: boolean
  description: string
  action: () => void
  enabled?: boolean // conditional enabling
  category: 'Navigation' | 'Actions' | 'Search' | 'UI' | 'Global'
  isSequence?: boolean
  sequenceKeys?: string[] // e.g., ['g', 'd']
}

interface UseKeyboardShortcutsOptions {
  onOpenCommandPalette?: () => void
  onOpenShortcutsHelp?: () => void
  onToggleSidebar?: () => void
}

/**
 * Check if user is currently typing in an input/textarea
 */
function isInputFocused(): boolean {
  const active = document.activeElement
  if (!active) return false

  return (
    active.tagName === 'INPUT' ||
    active.tagName === 'TEXTAREA' ||
    (active instanceof HTMLElement && active.isContentEditable)
  )
}

/**
 * Check if command palette or modal is open
 */
function isModalOpen(): boolean {
  // Check for command palette or modal backdrops
  return (
    document.querySelector('.command-palette-backdrop') !== null ||
    document.querySelector('.shortcuts-help-backdrop') !== null ||
    document.querySelector('[role="dialog"]') !== null
  )
}

/**
 * Central keyboard shortcuts manager
 * Registers global shortcuts and handles key events
 */
export function useKeyboardShortcuts(options: UseKeyboardShortcutsOptions = {}) {
  const navigate = useNavigate()
  const location = useLocation()
  const [sequenceBuffer, setSequenceBuffer] = useState<string[]>([])
  const sequenceTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const [showSequenceHint, setShowSequenceHint] = useState(false)

  // Clear sequence buffer after timeout
  useEffect(() => {
    if (sequenceBuffer.length > 0) {
      setShowSequenceHint(true)

      if (sequenceTimeoutRef.current) {
        clearTimeout(sequenceTimeoutRef.current)
      }

      sequenceTimeoutRef.current = setTimeout(() => {
        setSequenceBuffer([])
        setShowSequenceHint(false)
      }, 2000)
    }

    return () => {
      if (sequenceTimeoutRef.current) {
        clearTimeout(sequenceTimeoutRef.current)
      }
    }
  }, [sequenceBuffer])

  // Define all shortcuts (memoized to prevent infinite re-render loops)
  const shortcuts: KeyboardShortcut[] = useMemo(() => [
    // ===== Global Shortcuts =====
    {
      key: 'k',
      ctrl: true,
      description: 'Open command palette',
      action: () => options.onOpenCommandPalette?.(),
      category: 'Global',
    },
    {
      key: '/',
      ctrl: true,
      description: 'Show keyboard shortcuts help',
      action: () => options.onOpenShortcutsHelp?.(),
      category: 'Global',
    },
    {
      key: '?',
      shift: true,
      description: 'Show keyboard shortcuts help',
      action: () => options.onOpenShortcutsHelp?.(),
      category: 'Global',
    },
    {
      key: 'escape',
      description: 'Close modal/dialog or clear search',
      action: () => {
        // Close any open modals
        const closeButton = document.querySelector('[aria-label="Close"]') as HTMLButtonElement
        if (closeButton) {
          closeButton.click()
        }

        // Clear search if focused
        const searchInput = document.activeElement as HTMLInputElement
        if (searchInput?.type === 'search' || searchInput?.placeholder?.toLowerCase().includes('search')) {
          searchInput.value = ''
          searchInput.dispatchEvent(new Event('input', { bubbles: true }))
        }
      },
      category: 'Global',
    },

    // ===== Navigation Shortcuts (g → key) =====
    {
      key: 'd',
      description: 'Go to Dashboard',
      action: () => navigate('/'),
      category: 'Navigation',
      isSequence: true,
      sequenceKeys: ['g', 'd'],
    },
    {
      key: 'c',
      description: 'Go to Components',
      action: () => navigate('/components'),
      category: 'Navigation',
      isSequence: true,
      sequenceKeys: ['g', 'c'],
    },
    {
      key: 'f',
      description: 'Go to Failure Modes',
      action: () => navigate('/cause-effect'),
      category: 'Navigation',
      isSequence: true,
      sequenceKeys: ['g', 'f'],
    },
    {
      key: 'm',
      description: 'Go to Mitigations',
      action: () => navigate('/mitigations'),
      category: 'Navigation',
      isSequence: true,
      sequenceKeys: ['g', 'm'],
    },
    {
      key: 'r',
      description: 'Go to Requirements',
      action: () => navigate('/requirements'),
      category: 'Navigation',
      isSequence: true,
      sequenceKeys: ['g', 'r'],
    },

    // ===== Action Shortcuts =====
    {
      key: 'n',
      ctrl: true,
      description: 'Create new item (context-aware)',
      action: () => {
        // Context-aware creation based on current page
        const path = location.pathname

        if (path.includes('/components')) {
          // Trigger create component
          const createButton = document.querySelector('[data-action="create-component"]') as HTMLButtonElement
          createButton?.click()
          showToast.success('Create new component')
        } else if (path.includes('/cause-effect')) {
          // Trigger create failure mode
          const createButton = document.querySelector('[data-action="create-failure"]') as HTMLButtonElement
          createButton?.click()
          showToast.success('Create new failure mode')
        } else if (path.includes('/mitigations')) {
          // Trigger create mitigation
          const createButton = document.querySelector('[data-action="create-mitigation"]') as HTMLButtonElement
          createButton?.click()
          showToast.success('Create new mitigation')
        } else if (path.includes('/requirements')) {
          // Trigger create requirement
          const createButton = document.querySelector('[data-action="create-requirement"]') as HTMLButtonElement
          createButton?.click()
          showToast.success('Create new requirement')
        } else {
          // Fallback: open command palette
          options.onOpenCommandPalette?.()
        }
      },
      category: 'Actions',
    },
    {
      key: 's',
      ctrl: true,
      description: 'Save current form',
      action: () => {
        // Try to find and click a save button
        const saveButton = document.querySelector('[data-action="save"], [type="submit"]') as HTMLButtonElement
        if (saveButton) {
          saveButton.click()
          showToast.success('Saving...')
        }
      },
      category: 'Actions',
    },
    {
      key: 'e',
      ctrl: true,
      description: 'Export current view',
      action: () => {
        // Try to find and click an export button
        const exportButton = document.querySelector('[data-action="export"]') as HTMLButtonElement
        if (exportButton) {
          exportButton.click()
        } else {
          toast('Export functionality not available on this page', { icon: 'ℹ️' })
        }
      },
      category: 'Actions',
    },

    // ===== Search Shortcuts =====
    {
      key: '/',
      description: 'Focus search input',
      action: () => {
        // Find and focus the search input
        const searchInput = document.querySelector('[data-search-input], input[type="search"], input[placeholder*="Search" i]') as HTMLInputElement
        if (searchInput) {
          searchInput.focus()
          searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
        } else {
          toast('No search input found on this page', { icon: 'ℹ️' })
        }
      },
      category: 'Search',
    },

    // ===== UI Shortcuts =====
    {
      key: 'b',
      ctrl: true,
      description: 'Toggle sidebar',
      action: () => {
        options.onToggleSidebar?.()
        showToast.success('Sidebar toggled')
      },
      category: 'UI',
    },
  ], [navigate, location.pathname])

  // Use refs to store the latest values to avoid stale closure issues
  const shortcutsRef = useRef(shortcuts)
  const sequenceBufferRef = useRef(sequenceBuffer)

  // Keep refs in sync
  shortcutsRef.current = shortcuts
  sequenceBufferRef.current = sequenceBuffer

  // Register global keyboard event listener
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't intercept shortcuts when typing in inputs (except Escape)
      if (event.key !== 'Escape' && isInputFocused()) {
        return
      }

      // Don't intercept shortcuts when modals are open (except Escape)
      if (event.key !== 'Escape' && isModalOpen()) {
        return
      }

      const key = event.key.toLowerCase()
      const ctrl = event.ctrlKey || event.metaKey // metaKey = Cmd on Mac
      const shift = event.shiftKey
      const alt = event.altKey

      // Use refs to get latest values
      const currentShortcuts = shortcutsRef.current
      const currentSequenceBuffer = sequenceBufferRef.current

      // Handle sequence shortcuts (g → key)
      if (key === 'g' && !ctrl && !shift && !alt) {
        event.preventDefault()
        setSequenceBuffer(['g'])
        toast('Press d (Dashboard), c (Components), f (Failures), m (Mitigations), or r (Requirements)', {
          duration: 2000,
          icon: '⌨️',
        })
        return
      }

      // Check if we're in a sequence
      if (currentSequenceBuffer.length > 0) {
        event.preventDefault()
        const newSequence = [...currentSequenceBuffer, key]
        setSequenceBuffer(newSequence)

        // Find matching sequence shortcut
        const matchingShortcut = currentShortcuts.find(
          (shortcut) =>
            shortcut.isSequence &&
            shortcut.sequenceKeys &&
            JSON.stringify(shortcut.sequenceKeys) === JSON.stringify(newSequence)
        )

        if (matchingShortcut) {
          // Execute shortcut
          matchingShortcut.action()
          setSequenceBuffer([])
          setShowSequenceHint(false)
          if (sequenceTimeoutRef.current) {
            clearTimeout(sequenceTimeoutRef.current)
          }
        }
        return
      }

      // Check for regular shortcuts
      for (const shortcut of currentShortcuts) {
        if (shortcut.isSequence) continue // Skip sequence shortcuts

        // Check if enabled
        if (shortcut.enabled === false) continue

        // Match modifiers
        const ctrlMatch = shortcut.ctrl ? ctrl : !ctrl
        const shiftMatch = shortcut.shift ? shift : !shift
        const altMatch = shortcut.alt ? alt : !alt

        // Check if key matches
        const keyMatch = key === shortcut.key.toLowerCase()

        if (keyMatch && ctrlMatch && shiftMatch && altMatch) {
          event.preventDefault()
          shortcut.action()
          return
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      if (sequenceTimeoutRef.current) {
        clearTimeout(sequenceTimeoutRef.current)
      }
    }
  }, []) // Empty dependency array - handler uses refs instead

  // Return shortcuts for display in help modal
  return {
    shortcuts,
    showSequenceHint,
    sequenceBuffer,
  }
}
